/**
 * Holidays — consolidated hub for statutory + company-specific holidays.
 *
 * First slice of the P1-5 settings consolidation (see plans/settings-
 * consolidation.md). The two sub-pages kept their standalone routes
 * for backwards compatibility via redirects in App.tsx — deep links
 * can target this hub with `#public` or `#company`.
 */

import { useEffect, useState, SyntheticEvent, lazy, Suspense } from 'react';
import { Box, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import PageHelpIcon from '@/components/PageHelpIcon';

const PublicHolidaysPage = lazy(() => import('./PublicHolidaysPage'));
const CompanyHolidaysPage = lazy(() => import('./CompanyHolidaysPage'));

const TAB_BY_HASH: Record<string, number> = {
  '#public': 0,
  '#company': 1,
};
const HASH_BY_TAB = ['#public', '#company'];

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <Box role="tabpanel" hidden={value !== index}>
      {value === index && (
        <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}>
          {children}
        </Suspense>
      )}
    </Box>
  );
}

export default function HolidaysPage() {
  const [tab, setTab] = useState<number>(() => TAB_BY_HASH[window.location.hash] ?? 0);

  // Two-way sync between `tab` and `window.location.hash` so deep links
  // land on the right tab and tab clicks produce shareable URLs.
  useEffect(() => {
    const current = window.location.hash;
    const desired = HASH_BY_TAB[tab];
    if (desired && current !== desired) {
      window.history.replaceState(null, '', `${window.location.pathname}${desired}`);
    }
  }, [tab]);

  useEffect(() => {
    const onHashChange = () => {
      const next = TAB_BY_HASH[window.location.hash];
      if (typeof next === 'number') setTab(next);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, px: 3, pt: 2 }}>
        <CalendarToday color="primary" />
        <Typography variant="h5" fontWeight={TYPE.semibold}>
          Holidays
        </Typography>
        <PageHelpIcon pageKey="settings.holidays" />
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs
          value={tab}
          onChange={(_: SyntheticEvent, v: number) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Public holidays" />
          <Tab label="Company holidays" />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>
        <PublicHolidaysPage />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <CompanyHolidaysPage />
      </TabPanel>
    </Box>
  );
}
